class QSysChangeGroup {
    constructor(name) {
        this.name = name;
        this.componentControls = [];
        this.controls = [];
    }
    addComponentControl(component, controls) {
        for (const control of controls) {
            if (control.Name === undefined)
                continue;
            let found = false;
            for (const cc of this.componentControls) {
                if (cc.component === component) {
                    for (const ctrl of cc.controls) {
                        if (ctrl.name === control.Name)
                            found = true;
                    }
                    if (!found) {
                        cc.controls.push({ name: control.Name, modified: 0 });
                        found = true;
                    }
                }
            }
            if (!found) {
                this.componentControls.push({
                    component,
                    controls: [{ name: control.Name, modified: 0 }],
                });
            }
        }
    }
    addControl(controls) {
        for (const control of controls) {
            const ctrl = this.controls.find(ctrl => ctrl.name === control);
            if (ctrl === undefined)
                this.controls.push({ name: control, modified: 0 });
        }
    }
    clear() {
        this.componentControls.length = 0;
    }
    invalidate() {
        for (const cc of this.componentControls) {
            for (const ctrl of cc.controls) {
                ctrl.modified = 0;
            }
        }
        for (const ctrl of this.controls) {
            ctrl.modified = 0;
        }
    }
    poll(components, controls) {
        const result = {
            Id: this.name,
            Changes: [],
        };
        for (const cc of this.componentControls) {
            const comp = components.find(comp => comp.name === cc.component);
            if (comp === undefined)
                continue;
            for (const control of cc.controls) {
                const ctrl = comp.getControl(control.name);
                if (ctrl === undefined)
                    continue;
                if (ctrl.modified > control.modified) {
                    result.Changes.push(Object.assign({ Component: cc.component }, ctrl.getValue()));
                    control.modified = Date.now();
                }
            }
        }
        for (const control of this.controls) {
            const ctrl = controls.find(ctrl => ctrl.name === control.name);
            if (ctrl === undefined)
                continue;
            if (ctrl.modified > control.modified) {
                result.Changes.push(ctrl.getValue());
                control.modified = Date.now();
            }
        }
        return result;
    }
}
class QSysControlBase {
    constructor(name, value, onChange) {
        this.name = name;
        this.value = value;
        this.onChange = onChange;
        this._modified = Date.now();
    }
    get modified() {
        return this._modified;
    }
}
class QSysBooleanControl extends QSysControlBase {
    getValue() {
        return {
            Name: this.name,
            String: this.value ? 'true' : 'false',
            Value: this.value ? 1 : 0,
        };
    }
    setValue(value) {
        var _a;
        if (typeof value === 'boolean') {
            this.value = value;
        }
        else if (typeof value === 'string') {
            this.value = value === 'true';
        }
        else {
            this.value = value !== 0;
        }
        this._modified = Date.now();
        (_a = this.onChange) === null || _a === void 0 ? void 0 : _a.call(this, this.value);
    }
}
class QSysGainControl extends QSysControlBase {
    getValue() {
        return { Name: this.name, String: `${this.value}dB`, Value: this.value };
    }
    setValue(value) {
        var _a;
        if (typeof value === 'boolean') {
            if (!value)
                this.value = 0;
        }
        else if (typeof value === 'string') {
            const newValue = parseFloat(value);
            if (!Number.isNaN(newValue))
                this.value = newValue;
        }
        else {
            this.value = value;
        }
        this._modified = Date.now();
        (_a = this.onChange) === null || _a === void 0 ? void 0 : _a.call(this, this.value);
    }
}
class QSysMuteControl extends QSysControlBase {
    getValue() {
        return {
            Name: this.name,
            String: this.value ? 'muted' : 'unmuted',
            Value: this.value ? 1 : 0,
        };
    }
    setValue(value) {
        var _a;
        if (typeof value === 'boolean') {
            this.value = value;
        }
        else if (typeof value === 'string') {
            this.value = ['muted', 'true'].includes(value);
        }
        else {
            this.value = value !== 0;
        }
        this._modified = Date.now();
        (_a = this.onChange) === null || _a === void 0 ? void 0 : _a.call(this, this.value);
    }
}
class QSysStringControl extends QSysControlBase {
    getValue() {
        return { Name: this.name, String: this.value, Value: this.value };
    }
    setValue(value) {
        var _a;
        this.value = value.toString();
        this._modified = Date.now();
        (_a = this.onChange) === null || _a === void 0 ? void 0 : _a.call(this, this.value);
    }
}
class QSysComponent {
    constructor(name, type, controls) {
        this.name = name;
        this.type = type;
        this.controls = controls;
    }
    addControl(control) {
        this.controls.push(control);
    }
    getComponentInfo() {
        return {
            ID: this.name,
            Name: this.name,
            Type: this.type,
            Controls: null,
            ControlSource: 2,
            Properties: [],
        };
    }
    getControl(name) {
        const control = this.controls.find(control => control.name === name);
        if (control === undefined)
            return undefined;
        return control;
    }
}
class QSysGainComponent extends QSysComponent {
    constructor(name) {
        super(name, 'gain', [new QSysGainControl('gain', 0), new QSysMuteControl('mute', false)]);
    }
}
class QSysSoftPhoneComponent extends QSysComponent {
    constructor(name) {
        super(name, 'softphone', []);
        const callStatusControl = new QSysStringControl('call_status', 'Idle');
        const callConnectControl = new QSysBooleanControl('call_connect', true, connected => {
            if (this.dialString === undefined) {
                callStatusControl.setValue('Connected');
            }
            else {
                callStatusControl.setValue(`Connected - ${this.dialString}`);
            }
        });
        const callNumberControl = new QSysStringControl('call_number', '', dialString => {
            this.dialString = dialString;
        });
        const callDisconnectControl = new QSysBooleanControl('call_disconnect', true, disconnected => {
            this.dialString = undefined;
            callStatusControl.setValue('Idle');
        });
        this.addControl(callStatusControl);
        this.addControl(callConnectControl);
        this.addControl(callNumberControl);
        this.addControl(callDisconnectControl);
    }
}
class QSysTelephonyReceiverComponent extends QSysComponent {
    constructor(name) {
        super(name, 'telephony_receiver', [new QSysGainControl('channel_1_gain', 0)]);
    }
}
export class QSys {
    constructor(socket, log) {
        this.socket = socket;
        this.changeGroups = [];
        this.components = [
            new QSysGainComponent('Mics'),
            new QSysGainComponent('Amplifier'),
            new QSysGainComponent('Overflow Room A'),
            new QSysGainComponent('Overflow Room B'),
            new QSysGainComponent('Assistive Listening'),
            new QSysGainComponent('Audio Input A'),
            new QSysGainComponent('Audio Input B'),
            new QSysGainComponent('Audio Input C'),
            new QSysGainComponent('Sound Reinforcement'),
            new QSysSoftPhoneComponent('SoftPhone Status'),
            new QSysTelephonyReceiverComponent('SoftPhone In'),
        ];
        this.controls = [
            new QSysGainControl('Zone2:GainGain', 0),
            new QSysMuteControl('Zone2:GainMute', false),
        ];
        this.asyncHandlers = [
            {
                match: (req) => req.method === 'ChangeGroup.AddComponentControl' ? req : undefined,
                callback: (req) => {
                    var _a, _b, _c, _d, _e, _f;
                    const id = (_a = req.params) === null || _a === void 0 ? void 0 : _a.Id;
                    const component = (_c = (_b = req.params) === null || _b === void 0 ? void 0 : _b.Component) === null || _c === void 0 ? void 0 : _c.Name;
                    const controls = (_f = (_e = (_d = req.params) === null || _d === void 0 ? void 0 : _d.Component) === null || _e === void 0 ? void 0 : _e.Controls) !== null && _f !== void 0 ? _f : [];
                    if (id !== undefined && component !== undefined) {
                        let cg = this.changeGroups.find(cg => cg.name === id);
                        if (cg === undefined) {
                            cg = new QSysChangeGroup(id);
                            this.changeGroups.push(cg);
                        }
                        cg.addComponentControl(component, controls);
                    }
                    this.sendQrcResp(req, true);
                },
            },
            {
                match: (req) => req.method === 'ChangeGroup.AddControl' ? req : undefined,
                callback: (req) => {
                    var _a, _b, _c;
                    const id = (_a = req.params) === null || _a === void 0 ? void 0 : _a.Id;
                    const controls = (_c = (_b = req.params) === null || _b === void 0 ? void 0 : _b.Controls) !== null && _c !== void 0 ? _c : [];
                    if (id !== undefined) {
                        let cg = this.changeGroups.find(cg => cg.name === id);
                        if (cg === undefined) {
                            cg = new QSysChangeGroup(id);
                            this.changeGroups.push(cg);
                        }
                        cg.addControl(controls);
                    }
                    this.sendQrcResp(req, true);
                },
            },
            {
                match: (req) => (req.method === 'ChangeGroup.Destroy' ? req : undefined),
                callback: (req) => {
                    var _a;
                    const name = (_a = req.params) === null || _a === void 0 ? void 0 : _a.Id;
                    if (name !== undefined) {
                        const index = this.changeGroups.findIndex(cg => cg.name === name);
                        if (index >= 0)
                            this.changeGroups.splice(index, 1);
                    }
                    this.sendQrcResp(req, true);
                },
            },
            {
                match: (req) => (req.method === 'ChangeGroup.Poll' ? req : undefined),
                callback: (req) => {
                    var _a, _b;
                    const id = (_b = (_a = req.params) === null || _a === void 0 ? void 0 : _a.Id) !== null && _b !== void 0 ? _b : '';
                    const cg = this.changeGroups.find(cg => cg.name === id);
                    if (cg === undefined) {
                        this.sendQrcResp(req, {
                            error: {
                                code: 6,
                                message: `Change group '${id}' does not exist`,
                            },
                        });
                    }
                    else {
                        this.sendQrcResp(req, cg.poll(this.components, this.controls));
                    }
                },
            },
            {
                match: (req) => (req.method === 'Component.Get' ? req : undefined),
                callback: (req) => {
                    var _a, _b, _c, _d;
                    const name = (_a = req.params) === null || _a === void 0 ? void 0 : _a.Name;
                    if (name === undefined)
                        return;
                    const controls = (_c = (_b = req.params) === null || _b === void 0 ? void 0 : _b.Controls) !== null && _c !== void 0 ? _c : [];
                    const component = this.components.find(comp => comp.name === name);
                    if (!component)
                        return;
                    const controlResults = [];
                    for (const control of controls) {
                        const ctrl = component.getControl((_d = control.Name) !== null && _d !== void 0 ? _d : '');
                        if (ctrl !== undefined)
                            controlResults.push(ctrl.getValue());
                    }
                    this.sendQrcResp(req, { Name: name, Controls: controlResults });
                },
            },
            {
                match: (req) => req.method === 'Component.GetComponents' ? req : undefined,
                callback: (req) => {
                    const result = this.components.map(comp => comp.getComponentInfo());
                    this.sendQrcResp(req, result);
                },
            },
            {
                match: (req) => (req.method === 'Component.Set' ? req : undefined),
                callback: (req) => {
                    var _a, _b, _c, _d, _e;
                    const name = (_a = req.params) === null || _a === void 0 ? void 0 : _a.Name;
                    if (name === undefined)
                        return;
                    const controls = (_c = (_b = req.params) === null || _b === void 0 ? void 0 : _b.Controls) !== null && _c !== void 0 ? _c : [];
                    const component = this.components.find(comp => comp.name === name);
                    if (!component)
                        return;
                    for (const control of controls) {
                        const ctrl = component.getControl((_d = control.Name) !== null && _d !== void 0 ? _d : '');
                        if (ctrl !== undefined) {
                            ctrl.setValue((_e = control.Value) !== null && _e !== void 0 ? _e : 0);
                        }
                    }
                    this.sendQrcResp(req, true);
                },
            },
            {
                match: (req) => (req.method === 'Control.Get' ? req : undefined),
                callback: (req) => {
                    var _a;
                    const result = [];
                    for (const param of (_a = req.params) !== null && _a !== void 0 ? _a : []) {
                        const ctrl = this.controls.find(ctrl => ctrl.name === param);
                        if (ctrl === undefined)
                            continue;
                        result.push(ctrl.getValue());
                    }
                    this.sendQrcResp(req, result);
                },
            },
            {
                match: (req) => (req.method === 'Control.Set' ? req : undefined),
                callback: (req) => {
                    var _a, _b;
                    const ctrl = this.controls.find(ctrl => { var _a, _b; return ctrl.name === ((_b = (_a = req.params) === null || _a === void 0 ? void 0 : _a.Name) !== null && _b !== void 0 ? _b : ''); });
                    if (ctrl !== undefined)
                        ctrl.setValue((_b = (_a = req.params) === null || _a === void 0 ? void 0 : _a.Value) !== null && _b !== void 0 ? _b : 0);
                    this.sendQrcResp(req, true);
                },
            },
            {
                match: (req) => (req.method === 'StatusGet' ? req : undefined),
                callback: (req) => {
                    this.sendQrcResp(req, {
                        Platform: 'Emulator',
                        State: 'Active',
                        DesignName: 'qsys-Conf-System-VoIP-AV-USB-v1.25',
                        DesignCode: 'FKm9nwzZae5m',
                        IsRedundant: false,
                        IsEmulator: true,
                        Status: {
                            Code: 5,
                            String: 'Initializing - 4 OK, 1 Initializing',
                        },
                    });
                },
            },
        ];
    }
    parser(command) {
        try {
            const resp = JSON.parse(command);
            return resp;
        }
        catch (err) {
            return undefined;
        }
    }
    sendQrcResp(req, result) {
        this.socket.send(JSON.stringify(Object.assign(Object.assign({ jsonrpc: '2.0' }, (req.id ? { id: req.id } : undefined)), { result })));
    }
}
QSys.emulatorType = 'socketEmulator';
QSys.key = '-K_FswIwXRBjEqTRASYT';
